import { useEffect, useState } from "react";
import { Menu, Download } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import FoxMotif from "@/components/site/FoxMotif";

const NAV_LINKS = [
  { id: "apps", label: "Featured" },
  { id: "portfolio", label: "Portfolio" },
  { id: "about", label: "About" },
  { id: "contact", label: "Contact" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeId, setActiveId] = useState("home");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const ids = ["home", "apps", "portfolio", "about", "contact"];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: 0 }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const handleAnchor = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <header
      data-testid="site-nav"
      className={`sticky top-0 z-50 w-full border-b transition-[box-shadow,background-color] ${
        scrolled
          ? "bg-[hsl(var(--background))]/95 backdrop-blur supports-[backdrop-filter]:bg-[hsl(var(--background))]/80 pf-shadow-sm"
          : "bg-[hsl(var(--background))]"
      }`}
    >
      <div className="pf-container flex h-16 items-center justify-between">
        <a
          href="#home"
          onClick={(e) => handleAnchor(e, "home")}
          data-testid="nav-logo"
          className="flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2 rounded-md"
          aria-label="Play Fox Studio home"
        >
          <div className="relative h-8 w-8">
            <FoxMotif size={32} testId="nav-fox-motif" />
          </div>
          <span className="pf-display text-[17px] tracking-tight text-[hsl(var(--foreground))]">
            Play Fox <span className="text-[#F35A1F]">Studio</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              onClick={(e) => handleAnchor(e, l.id)}
              data-testid={`nav-link-${l.id}`}
              data-active={activeId === l.id}
              className="pf-link-underline text-sm font-medium text-[hsl(var(--foreground))]/85 hover:text-[hsl(var(--foreground))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2 rounded-sm"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <Button
            data-testid="nav-download-button"
            onClick={(e) => handleAnchor(e, "apps")}
            className="h-10 rounded-xl bg-[#F35A1F] px-4 text-white hover:bg-[#D94A14] focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2"
          >
            <Download className="h-4 w-4 mr-1.5" />
            Get the App
          </Button>
        </div>

        {/* Mobile */}
        <div className="md:hidden flex items-center gap-2">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                data-testid="nav-mobile-menu-open"
                aria-label="Open menu"
                className="h-10 w-10 rounded-xl border-[hsl(var(--border))]"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88%] sm:max-w-sm">
              <SheetHeader>
                <SheetTitle className="pf-display">Play Fox Studio</SheetTitle>
                <SheetDescription className="text-left">
                  Small Apps, Big Joy.
                </SheetDescription>
              </SheetHeader>
              <div className="mt-6 flex flex-col gap-1">
                {NAV_LINKS.map((l) => (
                  <a
                    key={l.id}
                    href={`#${l.id}`}
                    onClick={(e) => {
                      handleAnchor(e, l.id);
                      setMobileOpen(false);
                    }}
                    data-testid={`nav-mobile-link-${l.id}`}
                    className="flex items-center justify-between rounded-lg px-3 py-3 text-base font-medium text-[hsl(var(--foreground))] hover:bg-[hsl(var(--accent))]"
                  >
                    {l.label}
                    <span aria-hidden className="pf-mono text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                      0{NAV_LINKS.indexOf(l) + 1}
                    </span>
                  </a>
                ))}
                <Button
                  data-testid="nav-mobile-download-button"
                  onClick={(e) => {
                    handleAnchor(e, "apps");
                    setMobileOpen(false);
                  }}
                  className="mt-4 h-11 rounded-xl bg-[#F35A1F] text-white hover:bg-[#D94A14]"
                >
                  <Download className="h-4 w-4 mr-1.5" />
                  Get Clever Canine
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Nav;
